import React from "react";
import { Box, Typography, Chip, Grid, Button, TextField, IconButton, Paper } from "@mui/material";
import { useSelector } from "react-redux";
import SelectableRadioGroupManager from "../../selectable-radio-group-manger";
import MicIcon from "@mui/icons-material/Mic";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";

const issues = ["Anxiety", "Stress", "Bereavement", "Anger", "Frustration", "Fear", "Homesick", "Other"];
const triggers = ["Comment", "Denial", "Request", "Argument", "Touch", "Teasing", "Disruption", "Conflict", "Other"];
const warningSigns = ["No signs", "Pacing", "Fidgeting", "Clenched", "Silence", "Shouting", "Tension", "Other"];

const SupportStatementManager = ({ task, onClose }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const allowedBehaviors = task?.behaviorRecords?.map((b) => b.behavior);

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>
        Physical Intervention / Restraint Supporting Statement
      </Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8} md={8}>
          <Typography sx={{ fontSize: "16px", color: "black", fontWeight: "bold", mb: 2 }}>
            Challenging Behavior
          </Typography>
          <Typography sx={{ fontSize: "14px", mb: 3 }}>
            Describe the events leading up to the challenging behavior – including the underlying issues, triggers,
            temperature, sounds, who was around and warning signs as well as the immediate precipitating events
          </Typography>

          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mb: 2 }}>Issues</Typography>

          <SelectableRadioGroupManager options={issues} allowedOptions={allowedBehaviors} />

          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mb: 2, mt: 3 }}>Triggers</Typography>

          <SelectableRadioGroupManager options={triggers} allowedOptions={allowedBehaviors} />

          {/* Warning Signs */}
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mb: 2, mt: 3 }}>
            Warning Signs *
          </Typography>

          <SelectableRadioGroupManager options={warningSigns} allowedOptions={allowedBehaviors} />

          <Grid container>
            <Grid item lg={6} md={6}>
              {/* Environmental Factors */}
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mt: 3 }}>
                Environmental Factors
              </Typography>
              <TextField fullWidth variant='outlined' value={task?.temperature} type='number' sx={{ mb: 3 }} />
            </Grid>
          </Grid>

          {/* Who was Around */}
          <Grid container>
            <Grid item lg={6} md={6}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black" }}>Who was Around *</Typography>
              <TextField
                value={task?.whoWasAround}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={4} md={4}>
          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mb: 2 }}>
              Incident Management
            </Typography>
            <Typography sx={{ fontSize: "14px", mb: 3 }}>Behavior Records</Typography>

            {task?.behaviorRecords?.length > 0 ? (
              <>
                {[
                  { title: "Issues", data: issues },
                  { title: "Triggers", data: triggers },
                  { title: "Warning Signs", data: warningSigns },
                ].map((category, index) => (
                  <Box key={index} sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold", mb: 1, color: "black" }}>
                      {category.title}
                    </Typography>
                    {task?.behaviorRecords
                      .filter((record) => category.data.includes(record.behavior))
                      .map((record, recordIndex) => (
                        <Paper
                          key={recordIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 1,
                            mb: 1,
                            borderRadius: 2,
                            border: "1px solid #ccc",
                          }}
                        >
                          <Typography sx={{ fontSize: "14px", color: "black" }}>
                            {record.audio ? (
                              record.behavior
                            ) : (
                              <>
                                {record.behavior}
                                <br />
                                {record.note}
                              </>
                            )}
                          </Typography>
                          <Box>
                            {record.audio && (
                              <>
                                <IconButton onClick={() => window.open(record.audio, "_blank")}>
                                  <MicIcon color='primary' />
                                </IconButton>
                                <IconButton onClick={() => window.open(record.audio, "_blank")}>
                                  <CloudDownloadIcon color='primary' />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Paper>
                      ))}
                  </Box>
                ))}
              </>
            ) : (
              <Typography sx={{ fontSize: "14px", color: "gray" }}>No incident records found.</Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,
          background: "#fff",
          padding: 2,
          zIndex: 10,
        }}
      >
        <Button variant='outlined' color='primary' onClick={onClose}>
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default SupportStatementManager;
